/*!
 * Lake Effect Lures — commerce behaviour.
 *
 * Progressive enhancement only: every feature here has a working no-JS fallback
 * already rendered by the matching `sections/main-*.liquid` file (a real anchor,
 * a real GET/POST form, or a real submit button). This script just makes those
 * interactions faster once it's safe to assume JS is present.
 *
 * Vanilla ES2019+, no build step, IIFE-wrapped, defer-safe (DOM is present by the
 * time this file runs because it's loaded with the `defer` attribute).
 */
(function () {
  'use strict';

  var DUR_FAST = 140;

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function fireCartUpdated(detail) {
    document.dispatchEvent(new CustomEvent('le:cart:updated', { detail: detail || {} }));
  }

  function readJSON(script) {
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      return null;
    }
  }

  /* ----------------------------------------------------------- variant picker */

  function initVariantPickers() {
    qsa(document, '[data-le-product-section]').forEach(function (section) {
      var productData = readJSON(qs(section, '[data-le-product-json]'));
      if (!productData) return;

      var sectionId = section.getAttribute('data-section-id');
      var optionSelects = qsa(section, '[data-le-option-select]');
      var idInput = qs(section, '[data-le-variant-id]');
      var galleryThumbs = qsa(section, '[data-le-gallery-thumb]');
      var variantRequestSequence = 0;

      function findVariant(selected) {
        return productData.variants.filter(function (variant) {
          var opts = [variant.option1, variant.option2, variant.option3];
          return selected.every(function (value, i) {
            return opts[i] === value;
          });
        })[0];
      }

      function currentSelection() {
        return optionSelects
          .slice()
          .sort(function (a, b) {
            return Number(a.getAttribute('data-option-position')) - Number(b.getAttribute('data-option-position'));
          })
          .map(function (select) {
            return select.value;
          });
      }

      function setVariantAvailability(variant) {
        var addButton = qs(section, '[data-le-add-to-cart]');
        var dynamicCheckout = qs(section, '[data-le-dynamic-checkout]');
        var status = qs(section, '[data-le-variant-status]');
        var available = !!variant && variant.available === true;

        if (idInput) idInput.value = variant ? variant.id : '';
        if (addButton) addButton.disabled = !available;
        if (dynamicCheckout) dynamicCheckout.hidden = !available;
        if (status) {
          status.hidden = available;
          if (variant) {
            status.textContent = variant.available
              ? ''
              : status.getAttribute('data-sold-out-label');
          } else {
            status.textContent = status.getAttribute('data-unavailable-label');
          }
        }
      }

      function activateGalleryMedia(mediaId) {
        if (!mediaId) return;
        var thumb = galleryThumbs.filter(function (el) {
          return String(el.getAttribute('data-media-id')) === String(mediaId);
        })[0];
        if (thumb) selectGalleryThumb(section, thumb);
      }

      function refreshSection(variant, requestSequence) {
        if (!sectionId) return;
        var url = window.location.pathname + '?variant=' + encodeURIComponent(variant.id) + '&section_id=' + encodeURIComponent(sectionId);
        fetch(url, { credentials: 'same-origin' })
          .then(function (res) {
            return res.ok ? res.text() : Promise.reject(res.status);
          })
          .then(function (html) {
            // A shopper can change more than one option before the first
            // Section Rendering API response arrives. Never let an older
            // response replace controls for the newer selection.
            if (requestSequence !== variantRequestSequence) return;
            if (!idInput || String(idInput.value) !== String(variant.id)) return;
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            ['price-row', 'buy-buttons'].forEach(function (key) {
              var incoming = parsed.querySelector('[data-le-section="' + key + '"]');
              var target = qs(section, '[data-le-section="' + key + '"]');
              if (incoming && target) {
                target.innerHTML = incoming.innerHTML;
              }
            });
            setVariantAvailability(variant);
          })
          .catch(function () {
            /* Network hiccup: the hidden `id` input is already correct, so
               Add to cart still works even if the price/stock display is stale. */
          });
      }

      function onVariantChange(variant) {
        variantRequestSequence += 1;
        setVariantAvailability(variant);
        if (!variant) return;

        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        if (variant.featured_media) {
          activateGalleryMedia(variant.featured_media.id);
        }

        refreshSection(variant, variantRequestSequence);
      }

      optionSelects.forEach(function (select) {
        select.addEventListener('change', function () {
          var variant = findVariant(currentSelection());
          // Shopify can expose option values that are not valid together. Keep
          // the shopper's selection visible while preventing an invalid add.
          onVariantChange(variant);
        });
      });
    });
  }

  /* --------------------------------------------------------------- gallery -- */

  function selectGalleryThumb(section, thumb) {
    var mediaId = thumb.getAttribute('data-media-id');
    qsa(section, '[data-le-gallery-thumb]').forEach(function (el) {
      var active = el === thumb;
      el.classList.toggle('le-gallery__thumb--active', active);
      el.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    qsa(section, '[data-le-gallery-slide]').forEach(function (slide) {
      slide.classList.toggle('le-gallery__slide--active', String(slide.getAttribute('data-media-id')) === String(mediaId));
    });
  }

  function initGalleries() {
    qsa(document, '[data-le-gallery]').forEach(function (gallery) {
      qsa(gallery, '[data-le-gallery-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function (event) {
          event.preventDefault();
          selectGalleryThumb(gallery, thumb);
        });
      });
    });
  }

  /* ------------------------------------------------------- auth hash panels - */

  function initAuthToggles() {
    qsa(document, '[data-le-auth-toggle]').forEach(function (toggle) {
      toggle.addEventListener('click', function (event) {
        var targetId = toggle.getAttribute('data-le-auth-toggle');
        var target = targetId && document.getElementById(targetId);
        if (!target) return;
        event.preventDefault();

        qsa(document, '.le-auth__panel--recover').forEach(function (panel) {
          panel.classList.remove('is-open');
        });
        if (target.classList.contains('le-auth__panel--recover')) {
          target.classList.add('is-open');
        }

        var expanded = target.classList.contains('le-auth__panel--recover')
          ? target.classList.contains('is-open')
          : true;
        qsa(document, '[data-le-auth-toggle="' + targetId + '"]').forEach(function (el) {
          el.setAttribute('aria-expanded', String(expanded));
        });

        var focusable = target.querySelector('input, button, select, textarea');
        if (focusable) focusable.focus();
      });
    });
  }

  /* --------------------------------------------------- address panel toggles */

  function initAddressToggles() {
    qsa(document, '[data-le-address-toggle]').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var targetId = toggle.getAttribute('data-le-address-toggle');
        var target = targetId && document.getElementById(targetId);
        if (!target) return;
        var willOpen = !target.classList.contains('is-open');
        target.classList.toggle('is-open', willOpen);
        if (willOpen) {
          var focusable = target.querySelector('input, select, textarea');
          if (focusable) focusable.focus();
        }
      });
    });
  }

  /* --------------------------------------------------------- filter rail --- */

  function initFacets() {
    qsa(document, '[data-le-filter-group]').forEach(function (group) {
      var toggle = qs(group, '[data-le-filter-toggle]');
      if (!toggle) return;
      toggle.setAttribute('aria-expanded', String(!group.hasAttribute('data-le-collapsed')));
      toggle.addEventListener('click', function () {
        var collapsed = group.hasAttribute('data-le-collapsed');
        if (collapsed) {
          group.removeAttribute('data-le-collapsed');
        } else {
          group.setAttribute('data-le-collapsed', '');
        }
        toggle.setAttribute('aria-expanded', String(collapsed));
      });
    });

    qsa(document, '[data-le-facets]').forEach(function (form) {
      var openButtons = qsa(document, '[data-le-facets-open][aria-controls="' + form.id + '"]');
      var closeButtons = qsa(document, '[data-le-facets-close]');
      var previousFocus = null;

      function isMobileFacets() {
        return window.matchMedia('(max-width: 989px)').matches;
      }

      function closeFacets() {
        form.classList.remove('is-open');
        document.documentElement.classList.remove('le-facets-open');
        openButtons.forEach(function (button) {
          button.setAttribute('aria-expanded', 'false');
        });
        if (previousFocus) previousFocus.focus();
      }

      function openFacets(button) {
        previousFocus = button;
        form.classList.add('is-open');
        document.documentElement.classList.add('le-facets-open');
        openButtons.forEach(function (item) {
          item.setAttribute('aria-expanded', 'true');
        });
        var targetId = button.getAttribute('data-le-facets-target');
        var target = targetId && document.getElementById(targetId);
        window.setTimeout(function () {
          if (target) {
            target.scrollIntoView({ block: 'start' });
            var control = target.matches('select') ? target : target.querySelector('button, select, input');
            if (control) control.focus({ preventScroll: true });
          } else {
            var close = qs(form, '[data-le-facets-close]');
            if (close) close.focus();
          }
        }, DUR_FAST);
      }

      openButtons.forEach(function (button) {
        button.addEventListener('click', function () {
          openFacets(button);
        });
      });
      closeButtons.forEach(function (button) {
        button.addEventListener('click', closeFacets);
      });
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && form.classList.contains('is-open')) closeFacets();
      });

      qsa(form, '[data-le-facet-input]').forEach(function (input) {
        input.addEventListener('change', function () {
          if (isMobileFacets()) return;
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.submit();
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------------ cart -- */

  function moneyFromCents(cents, format) {
    var amount = (cents / 100).toFixed(2);
    return format ? format.replace(/\{\{\s*amount\s*\}\}/, amount) : '$' + amount;
  }

  function updateLine(row, item) {
    var totalCell = qs(row, '[data-le-line-total]');
    if (totalCell) {
      var priceEl = qs(totalCell, '.le-price__value') || totalCell;
      priceEl.textContent = moneyFromCents(item.final_line_price, window.Shopify && window.Shopify.money_format);
    }
    var qtyInput = qs(row, '[data-le-qty-input]');
    if (qtyInput) qtyInput.value = item.quantity;
  }

  function updateCartTotals(cart) {
    var subtotal = qs(document, '[data-le-cart-subtotal]');
    if (subtotal) {
      subtotal.textContent = moneyFromCents(cart.total_price, window.Shopify && window.Shopify.money_format);
    }
  }

  function showEmptyCart(form) {
    var emptyState = qs(document, '[data-le-cart-empty-state]');
    if (!emptyState) return;
    form.hidden = true;
    emptyState.hidden = false;
  }

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cartChange(body) {
    return fetch(routesRoot() + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json();
    });
  }

  function initCart() {
    var form = qs(document, '[data-le-cart-form]');
    if (!form) return;

    qsa(form, '[data-le-cart-row]').forEach(function (row) {
      var lineKey = row.getAttribute('data-line-key');
      var qtyInput = qs(row, '[data-le-qty-input]');
      var cartRequestSequence = 0;

      if (qtyInput) {
        qtyInput.addEventListener('change', function () {
          var quantity = parseInt(qtyInput.value, 10);
          if (isNaN(quantity) || quantity < 0) quantity = 0;
          var requestSequence = ++cartRequestSequence;
          row.classList.add('le-cart__row--removing');
          cartChange({ id: lineKey, quantity: quantity })
            .then(function (cart) {
              if (requestSequence !== cartRequestSequence) return;
              row.classList.remove('le-cart__row--removing');
              if (quantity === 0) {
                row.remove();
                if (!qsa(form, '[data-le-cart-row]').length) showEmptyCart(form);
              } else {
                var item = cart.items.filter(function (i) {
                  return i.key === lineKey;
                })[0];
                if (item) updateLine(row, item);
              }
              updateCartTotals(cart);
              fireCartUpdated({ cart: cart });
            })
            .catch(function () {
              row.classList.remove('le-cart__row--removing');
              form.submit();
            });
        });
      }

      var removeLink = qs(row, '[data-le-cart-remove]');
      if (removeLink) {
        removeLink.addEventListener('click', function (event) {
          event.preventDefault();
          var requestSequence = ++cartRequestSequence;
          row.classList.add('le-cart__row--removing');
          cartChange({ id: lineKey, quantity: 0 })
            .then(function (cart) {
              if (requestSequence !== cartRequestSequence) return;
              row.remove();
              if (!qsa(form, '[data-le-cart-row]').length) showEmptyCart(form);
              updateCartTotals(cart);
              fireCartUpdated({ cart: cart });
            })
            .catch(function () {
              window.location.href = removeLink.href;
            });
        });
      }
    });
  }

  /* -------------------------------------------------------------- quick add - */

  /* `product-card.liquid` renders quick add as a real <form> posting to
     /cart/add, so it still works with JS off. Here we intercept the submit and
     do it over fetch instead, keeping the shopper on the page. */
  function initQuickAdd() {
    document.addEventListener('submit', function (event) {
      var form = event.target.closest && event.target.closest('form[data-le-quick-add]');
      if (!form) return;

      var idField = form.querySelector('[name="id"]');
      if (!idField || !idField.value) return; // let the browser post it normally

      event.preventDefault();

      var button = form.querySelector('button[type="submit"], [type="submit"]');
      var quantityField = form.querySelector('[name="quantity"]');
      var quantity = parseInt(quantityField && quantityField.value, 10) || 1;

      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      }

      function release() {
        if (!button) return;
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }

      fetch(routesRoot() + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: idField.value, quantity: quantity }] })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then(function (result) {
          release();
          if (!result.ok) {
            // Shopify rejected the line (out of stock, quantity rule). Fall back to
            // the real form post so the shopper sees Shopify's own error page
            // rather than a button that silently does nothing.
            form.submit();
            return;
          }
          if (button) {
            button.classList.add('is-added');
            window.setTimeout(function () {
              button.classList.remove('is-added');
            }, 1600);
          }
          fireCartUpdated({ item: result.data });
        })
        .catch(function () {
          release();
          form.submit();
        });
    });
  }

  /* ------------------------------------------------------------- share/copy - */

  function initShare() {
    qsa(document, '[data-le-copy-link]').forEach(function (button) {
      button.addEventListener('click', function () {
        var url = button.getAttribute('data-url') || window.location.href;
        var done = function () {
          button.classList.add('is-copied');
          window.setTimeout(function () {
            button.classList.remove('is-copied');
          }, DUR_FAST * 8);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done, done);
        } else {
          var temp = document.createElement('textarea');
          temp.value = url;
          temp.style.position = 'fixed';
          temp.style.opacity = '0';
          document.body.appendChild(temp);
          temp.select();
          try {
            document.execCommand('copy');
          } catch (err) {
            /* no-op: link is still visible/shareable via the address bar */
          }
          document.body.removeChild(temp);
          done();
        }
      });
    });
  }

  /* ------------------------------------------------------------------- boot - */

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  ready(function () {
    initVariantPickers();
    initGalleries();
    initAuthToggles();
    initAddressToggles();
    initFacets();
    initCart();
    initQuickAdd();
    initShare();
  });
})();
